import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin, Loader2, ArrowLeft, ShieldCheck } from 'lucide-react';

const AdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyOtp, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });

  // 2FA state
  const [otpStep, setOtpStep] = useState(false);
  const [adminId, setAdminId] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);

  const from = location.state?.from?.pathname || '/admin/dashboard';

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(loginData);

      if (result.requires_2fa) {
        setAdminId(result.admin_id);
        setEmailHint(result.email_hint);
        setOtpStep(true);
        return;
      }

      if (result.success) {
        navigate(from, { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;

    const newDigits = [...otpDigits];
    newDigits[index] = value;
    setOtpDigits(newDigits);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newDigits = pasted.split('');
      setOtpDigits(newDigits);
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otp = otpDigits.join('');
    if (otp.length !== 6) return;

    setLoading(true);
    try {
      const result = await verifyOtp(adminId, otp);
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setOtpDigits(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setOtpStep(false);
    setAdminId('');
    setEmailHint('');
    setOtpDigits(['', '', '', '', '', '']);
  };

  // Auto-submit when all 6 digits are entered
  useEffect(() => {
    if (otpStep && otpDigits.every(d => d !== '') && otpDigits.join('').length === 6 && !loading) {
      handleVerifyOtp({ preventDefault: () => {} });
    }
    // eslint-disable-next-line
  }, [otpDigits]);

  // Focus first OTP input when step changes
  useEffect(() => {
    if (otpStep) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [otpStep]);

  return (
    <div className="h-screen w-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 overflow-auto">
      <div className="max-w-md w-full flex flex-col gap-4">
        <Button
          variant="ghost"
          onClick={otpStep ? handleBackToLogin : () => navigate('/')}
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {otpStep ? 'Back to Login' : 'Back to Home'}
        </Button>

        <Card className="shadow-xl border-0" data-testid="admin-auth-card">
          <CardContent className="p-8">
            {!otpStep ? (
              <>
                <div className="flex items-center justify-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                    <MapPin className="w-7 h-7 text-white" />
                  </div>
                  <h1
                    className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent"
                    style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                  >
                    Admin Portal
                  </h1>
                </div>

                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="admin@example.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      disabled={loading || authLoading}
                      data-testid="login-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      disabled={loading || authLoading}
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                    disabled={loading || authLoading}
                    data-testid="login-submit-btn"
                  >
                    {(loading || authLoading) ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      'Login'
                    )}
                  </Button>
                </form>
              </>
            ) : (
              <>
                <div className="flex items-center justify-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-emerald-600 rounded-lg flex items-center justify-center mr-3">
                    <ShieldCheck className="w-7 h-7 text-white" />
                  </div>
                  <h1
                    className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent"
                    style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                  >
                    Verification
                  </h1>
                </div>

                <p className="text-center text-gray-600 mb-6">
                  A 6-digit code was sent to<br />
                  <strong>{emailHint}</strong>
                </p>

                <form onSubmit={handleVerifyOtp} className="space-y-6">
                  <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
                    {otpDigits.map((digit, i) => (
                      <Input
                        key={i}
                        ref={(el) => (inputRefs.current[i] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(i, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                        className="w-12 h-14 text-center text-2xl font-bold border-2 focus:border-green-500"
                        disabled={loading || authLoading}
                        data-testid={`otp-input-${i}`}
                      />
                    ))}
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                    disabled={loading || authLoading || otpDigits.some(d => !d)}
                    data-testid="otp-submit-btn"
                  >
                    {(loading || authLoading) ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      'Verify & Login'
                    )}
                  </Button>
                </form>

                <p className="text-center text-sm text-gray-500 mt-4">
                  Code expires in 5 minutes. Check your spam folder if not received.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminLogin;
